<template>
    <div>
        <div class="flex min-h-900 justify-center overflow-hidden">
            <div class="h-full flex flex-col w-full max-w-1920">
                <header class="flex-none flex items-end mb-32 h-64">
                    <div class="flex items-center pl-32 mr-16 -mb-64">
                        <avatar
                            :color="active_application.stageColor"
                            :image="active_application.image_url"
                            size="large"
                            class="relative z-20"
                            shape="round"
                        >
                            {{ active_application.initials }}
                        </avatar>
                    </div>

                    <div>
                        <div class="flex items-center mb-8">
                            <button :class="active_application.favorite ? 'hover:text-semantic-warning-light text-semantic-warning' : 'hover:text-semantic-warning text-primary-5'"
                                    :disabled="true"
                                    role="button"
                            >
                                <svg-icon class="mr-12 w-20 h-20" type="star"></svg-icon>
                            </button>
                            <h1 class="font-semibold text-heading-1 leading-none">{{ active_application.fullName }}</h1>
                        </div>
                        <span class="text-body text-primary-80 leading-none hover:text-secondary cursor-pointer transition duration-150">
                            {{ translated(active_job.title, locale) }}
                        </span>
                        <template v-if="active_application.referrer || active_application.referrer_source">
                            <span> |</span>
                            <span class="text-body text-primary-80">
                                {{ $t('applications.source') }}:
                                <a :class="active_application.referrer ? 'hover:text-secondary' : ''" :href="active_application.referrer || undefined"
                                   class="text-primary-80 no-underline leading-none transition duration-150" target="_blank">
                                    {{ active_application.referrer_source }}
                                </a>
                            </span>
                        </template>
                    </div>

                    <div class="flex flex-grow items-end justify-end">
                        <move-stage :application="active_application" :can="true"></move-stage>

                        <round-button
                            class="mr-4"
                            icon="email"
                            light></round-button>

                        <round-button
                            class="mr-4"
                            icon="calendar-add-on"
                            light></round-button>

                        <round-button
                            class="mr-4"
                            icon="edit"
                            light></round-button>

                        <round-button
                            class="mr-4"
                            icon="download"
                            light></round-button>

                        <round-button :title="$t('settings.menu.integrations')"
                                      class="mr-4"
                                      icon="extension"
                                      light></round-button>

                    </div>
                </header>

                <section class="flex-1 overflow-hidden bg-white mb-48 shadow-md rounded flex">
                    <div class="w-1/2 border-r border-primary-20 flex-none p-48 overflow-y-scroll">
                        <application-personal-info :application="active_application" :can-manage="true"></application-personal-info>

                        <application-motivation :application="active_application" :can-view="true"></application-motivation>

                        <application-questions :application_questions="active_application.dynamic_questions"
                                               :form_questions="active_application.stage.job.dynamic_form.questions"
                                               :questions="active_application.dynamic_questions">
                        </application-questions>

                        <application-questions :application_questions="active_application.dynamic_questions_interim"
                                               :form_questions="active_application.stage.job.dynamic_form_interim.questions"
                                               :questions="active_application.dynamic_questions_interim"></application-questions>

                        <application-scores :application="active_application" :can-manage="true" class="mb-32"></application-scores>

                        <application-interviews :application="active_application" :can-manage="true"></application-interviews>

                        <application-documents :application="active_application" :can-manage="true"></application-documents>

                        <application-experience :application="active_application" :can-manage="true"></application-experience>
                    </div>

                    <div class="w-1/2 flex flex-col">
                        <div class="p-16 border-b-2 border-primary-20">
                            <div class="flex items-center">
                                <div class="pl-8 mr-auto">
                                    <a :class="[ isSubscribed ? 'text-primary' : 'text-primary-20 hover:text-primary-80' ]"
                                       class="pt-4 flex items-center cursor-pointer trans-ease group"
                                    >
                                        <svg-icon class="max-w-16 max-h-16 mr-8" type="notifications"></svg-icon>
                                        <div
                                            :class="[ isSubscribed ? 'border-primary text-primary' : 'border-primary-20 text-primary-20 group-hover:border-primary-80 group-hover:text-primary-80' ]"
                                            class="border-b-2 border-dashed trans-ease"
                                        >
                                            <div class="leading-none mb-2">
                                                {{ isSubscribed ? $t('applications.turn_off_notifications') : $t('applications.turn_on_notifications') }}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                                <permission-users :target="active_application" class="pl-24 mr-16"/>
                                <button class="w-32 h-32 flex flex-col items-center justify-center rounded-32 bg-primary-20 text-white trans-ease hover:bg-primary-80">
                                    <svg-icon class="max-w-16 max-h-16" type="add"></svg-icon>
                                </button>
                            </div>
                        </div>

                        <div class="border-b-2 border-primary-20">
                            <div class="pt-16 px-16 flex items-center space-x-8 mb-16">
                                <button :class="[ 'bg-secondary text-white' ]"
                                        class="flex-1 rounded h-32 px-24 border border-secondary trans-ease whitespace-nowrap hover:bg-secondary hover:text-white"
                                >
                                    <span class="font-semibold first-letter:uppercase">{{ $t('applications.timeline') }}</span>
                                </button>
                                <button :class="[ 'text-secondary' ]"
                                        class="flex-1 rounded h-32 px-24 border border-secondary trans-ease whitespace-nowrap hover:bg-secondary hover:text-white"
                                >
                                    <span class="flex-grow font-semibold first-letter:uppercase">{{ $t('tasks.tasks') }}</span>
                                    <span>(0)</span>
                                </button>
                                <button :class="[ 'text-secondary opacity-50 cursor-not-allowed' ]"
                                        class="flex-1 rounded h-32 px-24 border border-secondary trans-ease whitespace-nowrap"
                                        disabled="disabled"
                                >
                                    <span class="font-semibold first-letter:uppercase">{{ $t('applications.related_applications_general') }}</span>
                                    <span>(0)</span>
                                </button>
                            </div>
                        </div>

                        <keep-alive>
                            <timeline ref="timeline" :application="active_application" />
                        </keep-alive>

                        <keep-alive>
                            <comment-box ref="commentBox" :application="active_application" />
                        </keep-alive>
                    </div>
                </section>
            </div>
        </div>

        <div class="w-full h-full bg-primary opacity-30 absolute inset-0"></div>

        <div class="fixed flex items-center justify-center w-screen h-screen inset-0 z-10">
            <div class="bg-white shadow-md rounded relative p-48 max-w-640">
                <popup-content :description="$t('free_trial.application.description')"
                               :progress="80"
                               :title="$t('free_trial.application.title')"
                               icon="assignment-ind"
                ></popup-content>

                <div class="mt-48 flex justify-center space-x-20">
                    <simple-button class="uppercase w-200"
                                   look="secondary"
                                   @click="$emit('previous')">
                        {{ $t('general.back') }}
                    </simple-button>

                    <simple-button class="uppercase w-200"
                                   display="secondary"
                                   look="primary"
                                   @click="$emit('next')">
                        {{ $t('jobs.next_step') }}
                    </simple-button>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import Application from '../../../models/Application';
import Job from '../../../models/Job';
import SvgIcon from '../SvgIcon.vue';
import Avatar from '../Avatar.vue';
import PermissionUsers from '../PermissionUsers.vue';
import ApplicationDocuments from '../../ApplicationDocuments.vue';
import ApplicationExperience from '../../ApplicationExperience.vue';
import MoveStage from '../MoveStage.vue';
import TasksDetail from '../TasksDetail.vue';
import SimpleButton from '../SimpleButton.vue';
import Timeline from '../../Timeline.vue';
import PopupContent from './PopupContent.vue';
import RoundButton from '../RoundButton.vue';
import ApplicationQuestions from '../../ApplicationQuestions.vue';
import ApplicationScores from '../../ApplicationScores.vue';
import ApplicationInterviews from '../../ApplicationInterviews.vue';
import CommentBox from '../CommentBox.vue';
import ApplicationMotivation from '../../ApplicationMotivation.vue';
import ApplicationPersonalInfo from '../../ApplicationPersonalInfo.vue';
import {mapState} from 'pinia';
import {useMainStore} from '../../../state/store';

export default {
    name: 'ApplicantDetail',
    components: {ApplicationPersonalInfo, ApplicationMotivation, CommentBox, ApplicationInterviews, ApplicationScores, ApplicationQuestions, RoundButton, PopupContent, Timeline, SimpleButton, TasksDetail, MoveStage, ApplicationExperience, ApplicationDocuments, PermissionUsers, Avatar, SvgIcon},
    props: {
        active_application: Application,
        active_job: Job,
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        userSubscription() {
            return this.active_application.subscriptions.find(subscription => {
                return subscription.user_id === this.currentUserId;
            });
        },
        isSubscribed() {
            return !!this.userSubscription;
        },
    },
};
</script>
